<template>
    <div class="container booking-container">
        <div class="page-heading">
            <h2 class="display-4 montserrat-bold mb-0">Registered Booking</h2>
            <div class="mt--2 mb-4 heading-small">Appointment on {{ displayedAppointmentDate }}</div>
        </div>
        
        <booking-heading :shop="shop" />
        
        <div class="row">
            <div class="col-lg-8">
                <div id="booking-info-container" class="shadow-reg mb-6">
                    <div class="row">
                        <div class="col-lg-7 col-sm-12">
                            <h6 class="heading-small text-muted text-uppercase montserrat mb-2">Booking Information</h6>
                            <div class="font-weight-400 text-default text-sm">
                                Booking Code: <span class="font-weight-700" v-text="booking.code"></span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Appointment Date and Time: <span class="font-weight-700" v-text="displayedAppointmentDate"></span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Date Booked: <span class="font-weight-700" v-text="displayedDateBooked"></span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Status: <span class="font-weight-700 text-capitalize" v-text="displayedStatus"></span>
                            </div>
                        </div>
                        <div class="col-lg-5 col-sm-12">
                            <h6 class="heading-small text-muted text-uppercase montserrat mb-2">Payment Information</h6>
                            <div class="font-weight-400 text-default text-sm">
                                Booking Voucher:
                                <span v-if="booking.discount_code" class="font-weight-700 text-capitalize" v-text="booking.discount_code"></span>
                                <span v-else class="font-weight-700 text-capitalize text-muted">None used</span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Points used:
                                    <span v-if="booking.points_used == 0" class="font-weight-700 text-capitalize">0.00</span>
                                    <span v-else class="font-weight-700 text-capitalize">{{ pointsUsed.toFixed(2) }} ({{ pointMechanic.toFixed(2) }} point(s) is equal to PHP 1.00)</span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Payment method: <span class="font-weight-700 text-capitalize" v-text="displayedPaymentMethod"></span>
                            </div>
                            <div class="font-weight-400 text-default text-sm">
                                Payment status:
                                <span v-if="isPaid" class="font-weight-700 text-capitalize">
                                    <span class="text-success">Paid</span> on {{ booking.paid_at }}
                                </span>
                                <span v-else-if="isPending" class="font-weight-700 text-capitalize text-warning">
                                    Processing <i class="fa fa-spinner fa-spin ml-1"></i>
                                </span>
                                <span v-else class="font-weight-700 text-capitalize text-danger">Unpaid</span>
                            </div>
                        </div>
                        <div id="booking-service-conatiner" class="">
                            <h6 class="heading-small text-muted booking-service-heading">Service Summary</h6>

                            <booking-item-pane :booking-item="bookingItem" v-for="(bookingItem, key) in booking.services" :key="key" @edit="showBookingItemEditForm"/>                                
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-4">
                <booking-payment-section ref="paymentSection" :booking="booking" />
                <button v-if="booking.status == 'new_booking' && !isPaid && !isPending && isOnlinePayment"  class="btn btn-success btn-block mt-4" @click="settlePayment">
                    <i class="fas fa-credit-card mr-2"></i> Settle Payment
                </button>
                <button v-if="booking.status == 'new_booking'"  class="btn btn-danger btn-block mt-4" @click="cancelBooking">
                    <i class="fa fa-ban mr-2"></i> Cancel Booking
                </button>
                <booking-cancellation-form v-if="booking.status == 'new_booking'" :booking-id="booking.id" @cancelled="cancelled" ref="cancellationForm" />
            </div>
        </div>

        <booking-item-modal ref="bookingItemUpdateForm" @updated="bookingItemUpdated" />
    </div>
</template>
<script>
import { route, get, patch, pageRoute } from "@utils/network";
import { 
    initializePayment as initializeGCashPayment,
    registerPayment as registerGCashPayment
} from "./helpers/payments/gcash";
import moment from "moment";
import mixins from "@utils/mixins";
import BookingHeading from "./BookingHeading";
import BookingItemPane from "./BookingItemPane";
import BookingItemModal from "./BookingItemModal";
import BookingPaymentSection from "./BookingPaymentSection";
const BookingCancellationForm = () => import(/* webpackChunkName: "booking" */ "./BookingCancellationForm.vue");

import '@styles/booking.css';

export default {
    name: "BookingsPage",

    components: {
        BookingHeading, BookingItemPane, BookingPaymentSection,
        BookingCancellationForm, BookingItemModal
    },

    computed: {
        displayedAppointmentDate() {
            return this.booking.appointment_datetime
                ? moment(this.booking.appointment_datetime).format("MMMM DD, YYYY hh:mm A")
                : moment().format("MMMM DD, YYYY hh:mm A");
        },
        displayedDateBooked() {
            return this.booking.created_at
                ? moment(this.booking.created_at).format("MMMM DD, YYYY hh:mm A")
                : moment().format("MMMM DD, YYYY hh:mm A");
        },
        displayedBookingType() {
            return this.booking.is_home ? "Home Booking" : "In-shop Booking";
        },
        displayedStatus() {
            return (this.booking.status || "status").replace("_", " ");
        },
        displayedPaymentMethod() {
            return (this.booking.payment_option || "payment_method").replace("_", " ");
        },
        shop() {
            return this.booking.shop || {};
        },
        
        pointsUsed() {
            return parseFloat(this.booking.points_used || 0);
        },
        pointMechanic() {
            return parseFloat(this.booking.point_mechanic || 0);
        },
        isPaid() {
            return !!this.booking.paid_at;
        },
        isPending() {
            return this.booking.payment_remark == "pending";
        },
        isOnlinePayment() {
            const onlinePaymentMethods = ["gcash"];
            return onlinePaymentMethods.indexOf(this.booking.payment_option) >= 0;
        },
    },

    data: () => ({
        booking: {},
    }),

    methods: {
        initializeBooking() {
            return this.fetchBooking(this.$route.params.id);
        },

        fetchBooking(id) {
            get(route("bookings/:id", { id }))
                .then(response => (this.booking = response.data.result.booking))
                .catch(error => this.presentError(error, "Please reload the page"))
        },

        cancelled(response) {

        },

        showBookingItemEditForm(bookingItem) {
            this.$refs.bookingItemUpdateForm.show(bookingItem);
        },

        bookingItemUpdated(response) {
            this.booking = response.data.result.booking;
        },

        cancelBooking() {
            this.$refs.cancellationForm.show();
        },


        handlePayments() {
            if (this.isPending) {
                return this.syncPaymentDetails();
            } else if (this.isPaid || this.isPending) {
                return;
            }
            const initializePayment = (parseInt(this.$route.query.initiate_payment) || 0) && this.isOnlinePayment;
            if (initializePayment) {
                return this.$nextTick(() => this.settlePayment());
            }
            
            if (this.$route.query.payment_status == "authorized" && this.$route.query.payment_type == "gcash") {
                return this.$nextTick(() => this.registerGCashPayment());
            }
        },

        settlePayment() {
            const amountDue = this.$refs.paymentSection.amountDue || 0;
            if (amountDue <= 0) return;

            if (this.booking.payment_option == "gcash") {
                this.settllePaymentViaGCash(amountDue);
            }
        },

        settllePaymentViaGCash(amountDue) {
            initializeGCashPayment(this.booking.id, amountDue)
                .catch(error => this.presentError(error));
        },

        registerGCashPayment() {
            if (!this.booking.id) return;

            registerGCashPayment(this.booking.id)
                .then(response => {
                    this.booking.payment_remark = "pending";
                    setTimeout(() => this.syncPaymentDetails(), 1000)
                })
                .catch(error => this.presentError(error));
        },

        syncPaymentDetails() {
            this.initializeBooking();
            setTimeout(() => {
                if (this.isPending) {
                    this.syncPaymentDetails();
                }
            }, 3000);
        },

        
    },

    mixins: [mixins],

    mounted() {
        this.initializeBooking();
        setTimeout(() => this.handlePayments(), 1000);
    }
};
</script>